import React, { useContext, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, Form, UncontrolledCollapse } from 'reactstrap'

import { API } from 'API'
import { UserAccessPermissionsEnum } from 'constants/userAccessPermissionsEnum'
import { RequestToasterContext } from 'containers/Providers'
import { getUserAuthorities } from 'core/auth'
import {
  AccessCountries,
  Country,
  GLOBAL_COUNTRY_PERMISSION,
  NO_REGION,
  UserRoles,
} from 'domains/user'
import { StandardErrorResponse } from 'types/APIResponses'

interface CountriesPermissionsControlProps {
  username: string
  userRole: UserRoles
}

type CountriesForm = Record<string, boolean>

export const CountriesPermissionsControl = ({
  username,
  userRole,
}: CountriesPermissionsControlProps) => {
  const [accessCountries, setAccessCountries] = useState<AccessCountries>({})
  const [countriesTotal, setCountriesTotal] = useState<number>(0)
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const form = useForm<CountriesForm>({
    mode: 'onSubmit',
  })
  const { requestStatusRef } = useContext(RequestToasterContext)
  const regionRefs = useRef(new Map())

  const isEditPermissionAllowed = getUserAuthorities().includes(
    UserAccessPermissionsEnum.SUPPORT_TOOL_EDIT_PERMISSIONS,
  )
  const isDoctor = userRole === UserRoles.ROLE_NORMAL

  const handleSelectAllCountries = () => {
    if (!isEditPermissionAllowed) return
    const selected = Object.values(form.getValues()).filter((isSelected) => isSelected)
    const isSelected = selected.length !== countriesTotal

    regionRefs.current.forEach((input: HTMLInputElement) => {
      input.checked = isSelected
      input.indeterminate = false
    })
    Object.values(accessCountries).forEach((region) => {
      region.forEach((country) => {
        form.setValue(country.isoCode, isSelected)
      })
    })
  }

  const setRegionCheckbox = (regionName: string) => {
    const regionChecked = accessCountries[regionName].some((country) =>
      form.getValues(`${country.isoCode}`),
    )
    const regionUnchecked = accessCountries[regionName].some(
      (country) => !form.getValues(`${country.isoCode}`),
    )

    regionRefs.current.get(regionName).checked = regionChecked
    regionRefs.current.get(regionName).indeterminate = regionChecked && regionUnchecked
  }

  const updateRegionRef = (input: HTMLInputElement | null, regionName: string) => {
    if (input) {
      regionRefs.current.set(regionName, input)
      setRegionCheckbox(regionName)
    } else {
      regionRefs.current.delete(regionName)
    }
  }

  const handleToggleRegion = (regionName: string) => {
    if (!isEditPermissionAllowed) return
    const regionChecked = Object.values(accessCountries[regionName]).every((country) =>
      form.getValues(`${country.isoCode}`),
    )
    const regionCountryCodes = Object.values(accessCountries[regionName]).map(
      (country) => country.isoCode,
    )

    regionCountryCodes.forEach((isoCode) => {
      form.setValue(isoCode, !regionChecked)
    })
    setRegionCheckbox(regionName)
  }

  const handleGetSearchCountries = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }

  const updatePermissions = () => {
    if (!isEditPermissionAllowed) return
    const selectedCodes: string[] = []

    Object.entries(form.getValues()).forEach(([isoCode, isSelected]) => {
      if (isSelected) {
        selectedCodes.push(isoCode)
      }
    })

    const updateData =
      selectedCodes.length === countriesTotal ? [GLOBAL_COUNTRY_PERMISSION] : selectedCodes

    API.updateUserCountriesPermissions(username, updateData)
      .then(() => {
        requestStatusRef.current?.showAlert('Updated', 'success')
        setSelectedCountryCodes(selectedCodes)
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  }

  const renderCountry = (country: Country, regionName?: string) => {
    if (search.trim() !== '') {
      if (!country.name.toLowerCase().startsWith(search.trim().toLowerCase())) return null
    }

    return (
      <div key={country.isoCode} className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={country.isoCode}
          disabled={!isEditPermissionAllowed}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...form.register(`${country.isoCode}`)}
          onChange={(field) => {
            form.setValue(country.isoCode, field.target.checked)
            if (regionName) setRegionCheckbox(regionName)
          }}
        />
        <label className="form-check-label" htmlFor={country.isoCode}>
          {country.name}
        </label>
      </div>
    )
  }

  const renderSelectedCountry = (country: Country, selected: boolean) => {
    if (selectedCountryCodes.includes(country.isoCode) !== selected) return null
    return renderCountry(country)
  }

  const renderRegion = (regionName: string, countries: Country[], selected: boolean) => {
    if (regionName === NO_REGION) {
      return countries.map((country) => renderSelectedCountry(country, selected))
    }

    const regionShouldRender = countries.some((country) =>
      selectedCountryCodes.includes(country.isoCode),
    )

    if (regionShouldRender !== selected) return null
    if (search.trim() !== '') {
      const containsSearchMatch = countries.some((country) =>
        country.name.toLowerCase().startsWith(search.trim().toLowerCase()),
      )

      if (!containsSearchMatch) return null
    }

    return (
      <div key={regionName}>
        <div className="form-check">
          <input
            ref={(el) => updateRegionRef(el, regionName)}
            className="form-check-input"
            type="checkbox"
            id={regionName}
            onChange={() => handleToggleRegion(regionName)}
            disabled={!isEditPermissionAllowed}
          />
          <label className="form-check-label" htmlFor={regionName}>
            {regionName} region
          </label>
          <a className="text-secondary dropdown-toggle p-2" id="toggler"></a>
        </div>
        <UncontrolledCollapse toggler="#toggler">
          <div className="pl-2">
            {countries.map((country) => renderCountry(country, regionName))}
          </div>
        </UncontrolledCollapse>
      </div>
    )
  }

  useEffect(() => {
    Promise.all([API.getCountriesPermissions(), API.getUserCountriesPermissions(username)])
      .then(([accessCountries, accessUserCountries]) => {
        const selectedCodes: string[] = []
        const initialSelectedCountries: CountriesForm = {}
        const countriesTotal = Object.values(accessCountries).reduce(
          (acc, region) => acc + region.length,
          0,
        )

        if (accessUserCountries.includes(GLOBAL_COUNTRY_PERMISSION)) {
          Object.values(accessCountries).forEach((region) => {
            region.forEach((country) => {
              selectedCodes.push(country.isoCode)
              initialSelectedCountries[country.isoCode] = true
            })
          })
        } else {
          Object.values(accessCountries).forEach((region) => {
            region.forEach((country) => {
              if (accessUserCountries.includes(country.isoCode)) {
                selectedCodes.push(country.isoCode)
                initialSelectedCountries[country.isoCode] = true
              } else {
                initialSelectedCountries[country.isoCode] = false
              }
            })
          })
        }

        setAccessCountries(accessCountries)
        setSelectedCountryCodes(selectedCodes)
        setCountriesTotal(countriesTotal)
        form.reset(initialSelectedCountries)
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormProvider {...form}>
      <div className="d-flex justify-content-between mb-2">
        <input
          type="text"
          name="search"
          value={search}
          onChange={handleGetSearchCountries}
          placeholder="Search"
          className="w-50"
          disabled={!isEditPermissionAllowed}
        />
        <Button
          type="button"
          color="link"
          size="sm"
          onClick={handleSelectAllCountries}
          disabled={!isEditPermissionAllowed}>
          Select all
        </Button>
      </div>

      <Form onSubmit={form.handleSubmit(updatePermissions)}>
        <div
          className="p-2 d-flex flex-column"
          style={{
            border: 'solid #dee2e6 1px',
            borderRadius: 4,
            maxHeight: 265,
            overflowX: 'auto',
            gap: '0.5rem',
          }}>
          {Object.entries(accessCountries).map(([regionName, countries]) =>
            renderRegion(regionName, countries, true),
          )}

          {selectedCountryCodes.length !== 0 && selectedCountryCodes.length < countriesTotal && (
            <hr className="w-100 mt-2 mb-2" />
          )}

          {Object.entries(accessCountries).map(([regionName, countries]) =>
            renderRegion(regionName, countries, false),
          )}
        </div>

        <div className="d-flex justify-content-end mt-3" style={{ gap: '1rem' }}>
          <button type="submit" disabled={!isEditPermissionAllowed || isDoctor}>
            Update Countries Permissions
          </button>
        </div>
      </Form>
    </FormProvider>
  )
}
